import "./AdvancedCalculatorUnit.scss";
// REACT; ROUTER
import React, { ChangeEvent, useEffect, useState } from "react";

// COMPONENTS & FUNCTIONS
import CalculatorFieldset from "./CalculatorFieldset";
import GraphUnit from "./GraphUnit";
import { addAnimation } from "../../functions/animationHandler";
import {
  calculateHandler,
  convertHandler,
} from "../../functions/mathHandler";
import { updateAppDataFromLocalStorage } from "../../functions/localStorageHandlers";
import { checkInput } from "../../functions/unitHandler";
import { removeFormatLargeNumbers } from "../../functions/textFormatHandler";
import { createAdvancedGraphSource } from "../../functions/graphHandler";

// SVG & IMAGES

// INTERFACES
import {
  AdvancedCalculatorInput,
  AdvancedCalculatorResult,
  AppData,
  BaseQuantity,
} from "../../interface/functionalityInterface";
import {
  AdvancedCalculatorUnitProps,
  ReceiptUnitProps,
} from "../../interface/propTypesInterface";

// NPM INSTALLS
import * as FaIcons from "react-icons/fa";
import { v4 as UUID } from "uuid";

function groupBySection<T extends { section?: string }>(items: T[]) {
  return items.reduce<{ section?: string; items: T[] }[]>(
    (groups, item) => {
    const previousGroup = groups[groups.length - 1];
    if (previousGroup && previousGroup.section === item.section) {
      previousGroup.items.push(item);
    } else {
      groups.push({ section: item.section, items: [item] });
    }
    return groups;
    },
    []
  );
}

/*
ADVANCEDCALCULATORUNIT COMPONENT INFORMATION
  Advanced calculators keep their calculation model in a separate definition
  under engineering/advanced-calculators.
  - Inputs are displayed in the user's selected unit and converted to their
    coherent SI unit before the calculator definition is called.
  - Outputs are returned in their coherent SI unit and converted only for display.
  - The same component renders the shared figure, equation, result fields,
    receipt controls and optional graph for every advanced calculator.
  - Calculation functions are restored from the definition registry after
    persisted calculator data is loaded from local storage.
*/

function AdvancedCalculatorUnit(props: AdvancedCalculatorUnitProps) {
  // ::::: CONSTANTS :::::
  const { removeUnit, duplicateUnit, addToReceipt } = props;

  // ::::: USEHOOKS :::::

  // Entry animation of calculator
  useEffect(() => {
    const calculator = document.getElementById(props.id);
    setcalcElem(calculator);
    if (!calculator) return;
    calculator.classList.add("show");
  }, [props.id]);

  // Change state if props changes
  useEffect(() => {
    setcalc({ ...props });
  }, [props]);

  // ::::: STATES :::::
  const [calc, setcalc] = useState<AdvancedCalculatorUnitProps>(props);
  const [calcElem, setcalcElem] = useState<HTMLElement | null>();
  const [flipImg, setFlipImg] = useState(false);
  const [showGraph, setShowGraph] = useState(false);

  const inputValues = calc.inputs.reduce<Record<string, number>>(
    (values, input) => {
      const evaluatedValue = calculateHandler(
        removeFormatLargeNumbers(String(input.value)).replace(",", ".")
      );

      values[input.name] =
        evaluatedValue === null
          ? NaN
          : convertHandler(
              evaluatedValue.toString(),
              input.baseUnit,
              input.selectedUnit,
              input.units
            );
      return values;
    },
    {}
  );

  const result: AdvancedCalculatorResult | null = calc.calculate(inputValues);
  const validationMessages = calc.validate?.(inputValues) ?? [];
  const graphSource = result
    ? createAdvancedGraphSource(calc, result)
    : null;

  const formatResult = (
    value: number,
    quantity: BaseQuantity,
    selectedUnit: string,
    decimalsOffset = 0
  ) =>
    new Intl.NumberFormat("en", {
      maximumFractionDigits: calc.noDecimals + decimalsOffset,
      minimumFractionDigits: calc.noDecimals + decimalsOffset,
    }).format(
      convertHandler(
        value.toString(),
        selectedUnit,
        quantity.baseUnit,
        quantity.units
      )
    );

  // ::::: HANDLER FUNCTIONS :::::
  const saveCalculator = (calculator: AdvancedCalculatorUnitProps) => {
    setcalc(calculator);
    updateAppDataFromLocalStorage(calculator.id, calculator);
  };

  const handleInputs = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const targetValue = removeFormatLargeNumbers(e.target.value).replace(
      ",",
      "."
    );

    if (!checkInput(targetValue, true)) {
      addAnimation(e.target, "shakeHorError");
      return;
    }

    saveCalculator({
      ...calc,
      inputs: calc.inputs.map((input) =>
        input.id === e.target.id
          ? { ...input, value: targetValue }
          : input
      ),
    });
  };

  const handleTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    saveCalculator({
      ...calc,
      name: e.target.value,
    });
  };

  const handleUnits = (
    formInput: BaseQuantity,
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedUnit = e.target.value;
    const calculator = {
      ...calc,
      inputs: calc.inputs.map((input) => {
        if (input.id !== formInput.id) return input;

        const evaluatedValue = calculateHandler(String(input.value));
        return {
          ...input,
          value:
            evaluatedValue === null || input.value === ""
              ? input.value
              : convertHandler(
                  evaluatedValue.toString(),
                  selectedUnit,
                  input.selectedUnit,
                  input.units
                ).toFixed(calc.noDecimals),
          selectedUnit,
        };
      }),
    };

    saveCalculator(calculator);
  };

  const handleResultUnit = (
    outputName: string,
    e: ChangeEvent<HTMLSelectElement>
  ) => {
    saveCalculator({
      ...calc,
      resultUnits: {
        ...calc.resultUnits,
        [outputName]: e.target.value,
      },
    });
  };

  const handleReceipt = () => {
    if (!result) {
      if (calcElem) addAnimation(calcElem, "shakeHorError");
      addToReceipt(null);
      return;
    }

    const receipt: ReceiptUnitProps = {
      id: UUID(),
      type: AppData.RECEIPT,
      label: calc.name,
      output: calc.outputs.map((output) => {
        const selectedUnit =
          calc.resultUnits[output.name] ?? output.selectedUnit;
        return {
          name: output.name.replaceAll("_", " "),
          value: formatResult(
            result.outputs[output.name],
            output,
            selectedUnit,
            output.decimalsOffset
          ),
          unit: selectedUnit,
        };
      }),
      timestamp: Date.now() / 1000,
      mode: "advanced",
      deleteCallback: () => {},
    };

    if (calcElem) addAnimation(calcElem, "shakeVertSuccess");
    addToReceipt(receipt);
  };

  const handleDecimal = (changeAmount: "increase" | "decrease") => {
    const noDecimals =
      changeAmount === "increase"
        ? Math.min(9, calc.noDecimals + 1)
        : Math.max(0, calc.noDecimals - 1);

    saveCalculator({
      ...calc,
      noDecimals,
    });
  };

  const handleClearCalc = () => {
    saveCalculator({
      ...calc,
      inputs: calc.inputs.map((input: AdvancedCalculatorInput) => ({
        ...input,
        value: input.clearValue ?? "",
      })),
    });

    if (calcElem) addAnimation(calcElem, "shakeVert");
  };

  const handleSaveGraph = (
    graphConfiguration: AdvancedCalculatorUnitProps["graphConfiguration"]
  ) => {
    saveCalculator({
      ...calc,
      graphConfiguration,
    });
  };

  // ::::: JSX :::::
  try {
    return (
      <div id={calc.id} className="calculator advanced-calculator">
        <form>
          {/* ---------------------- TOP TOOLBAR ----------------------*/}
          <div className="calculator__buttons">
            <button
              type="button"
              onClick={() => removeUnit(calc.id)}
              className="calculator__delete round-button"
              data-tooltip="delete calc"
              aria-label={`Delete ${calc.name} calculator`}
            >
              <FaIcons.FaTrash />
            </button>
            <button
              type="button"
              onClick={handleClearCalc}
              className="round-button"
              data-tooltip="clear calc"
              aria-label={`Clear ${calc.name} calculator`}
            >
              <FaIcons.FaBan />
            </button>
          </div>

          {/* ---------------------- FIGURE ----------------------*/}
          <div
            className={`calculator__figure ${
              flipImg && calc.equation ? "flip" : ""
            }`}
            onClick={() => setFlipImg(!flipImg)}
            style={calc.equation ? { cursor: "pointer" } : {}}
            data-tooltip={calc.equation ? "click to show equation" : ""}
          >
            {calc.figure ? (
              <img
                src={calc.figure}
                alt={`${calc.name} calculation figure`}
                draggable="false"
              />
            ) : (
              <span>{calc.name.substring(0, 5)}</span>
            )}
            {calc.equation && <code>{calc.equation}</code>}
          </div>

          {/* ---------------------- TITLE ----------------------*/}
          <fieldset className="calculator__title">
            <label>calculator name</label>
            <input
              value={calc.name}
              onChange={handleTitle}
              className="no-dropdown"
              style={{ textAlign: "center" }}
              placeholder="calculator name"
            />
          </fieldset>

          <span className="divider"></span>

          {/* ---------------------- INPUTS & OUTPUTS ----------------------*/}
          <div className="calculator__inputs advanced-calculator__content">
            <div className="advanced-calculator__input-list">
              {groupBySection(
                calc.inputs.filter((input) => input.type !== "hidden")
              ).map((group, groupIndex) => (
                <div
                  className="advanced-calculator__field-group"
                  key={`${group.section ?? "inputs"}-${groupIndex}`}
                >
                  {group.section && <h3>{group.section}</h3>}
                  {group.items.map((input) => (
                    <CalculatorFieldset
                      bq={input}
                      key={input.id}
                      handleInputs={handleInputs}
                      handleUnits={handleUnits}
                    />
                  ))}
                </div>
              ))}
            </div>

            <span
              className="advanced-calculator__content-divider"
              aria-hidden="true"
            ></span>

            <div
              className="advanced-calculator__outputs"
              aria-live="polite"
            >
              <h3>calculated results</h3>
              {groupBySection(calc.outputs).map((group, groupIndex) => (
                <div
                  className="advanced-calculator__field-group"
                  key={`${group.section ?? "outputs"}-${groupIndex}`}
                >
                  {group.section && <h4>{group.section}</h4>}
                  {group.items.map((output) => {
                    const selectedUnit =
                      calc.resultUnits[output.name] ??
                      output.selectedUnit;
                    return (
                      <fieldset
                        className="advanced-calculator__result-field"
                        key={output.id}
                      >
                        <label htmlFor={`${calc.id}-${output.id}`}>
                          {output.name.replaceAll("_", " ")}
                        </label>
                        <input
                          id={`${calc.id}-${output.id}`}
                          value={
                            result
                              ? formatResult(
                                  result.outputs[output.name],
                                  output,
                                  selectedUnit,
                                  output.decimalsOffset
                                )
                              : "—"
                          }
                          readOnly
                          aria-readonly="true"
                        />
                        <select
                          value={selectedUnit}
                          onChange={(e) =>
                            handleResultUnit(output.name, e)
                          }
                          aria-label={`${output.name.replaceAll(
                            "_",
                            " "
                          )} result unit`}
                        >
                          {output.units.map((unit) => (
                            <option value={unit.name} key={unit.name}>
                              {unit.name}
                            </option>
                          ))}
                        </select>
                      </fieldset>
                    );
                  })}
                </div>
              ))}
              {!result && (
                <p className="advanced-calculator__message">
                  Check the input values and required fields.
                </p>
              )}
              {validationMessages.map((message) => (
                <p className="advanced-calculator__message" key={message}>
                  {message}
                </p>
              ))}
            </div>
          </div>

          {/* ---------------------- BOTTOM TOOLBAR ----------------------*/}
          <div className="calculator__toolbar">
            <div style={{ display: "flex" }}>
              <button
                className="rectangular-button small inverse-color"
                onClick={handleReceipt}
                type="button"
                data-tooltip="save to receipt"
              >
                <FaIcons.FaReceipt />
              </button>
              <button
                type="button"
                onClick={() => duplicateUnit(calc)}
                className="rectangular-button small inverse-color"
                data-tooltip="duplicate calc"
              >
                <FaIcons.FaClone />
              </button>
              {graphSource && (
                <button
                  type="button"
                  onClick={() => setShowGraph(true)}
                  className="rectangular-button small inverse-color"
                  data-tooltip="show graph"
                >
                  <FaIcons.FaChartLine />
                </button>
              )}
            </div>

            {/* ---------------------- DECIMALS ----------------------*/}
            <div
              className="calculator__decimal"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <span>no. decimals: {calc.noDecimals}</span>
              <div style={{ display: "flex" }}>
                <div data-tooltip="decrease decimals">
                  <button
                    className="calculator__decimal-button rectangular-button smaller inverse-color"
                    type="button"
                    onClick={() => handleDecimal("decrease")}
                    aria-label="Decrease decimals"
                  >
                    <FaIcons.FaMinus />
                  </button>
                </div>
                <div data-tooltip="increase decimals">
                  <button
                    className="calculator__decimal-button rectangular-button smaller inverse-color"
                    type="button"
                    onClick={() => handleDecimal("increase")}
                    aria-label="Increase decimals"
                  >
                    <FaIcons.FaPlus />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>

        {showGraph && graphSource && (
          <GraphUnit
            source={graphSource}
            savedConfiguration={calc.graphConfiguration}
            saveConfiguration={handleSaveGraph}
            closeGraph={() => setShowGraph(false)}
          />
        )}
      </div>
    );
  } catch (error) {
    return (
      <div
        id={calc.id}
        className="calculator"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "30rem",
        }}
      >
        <h2 style={{ color: "var(--color-error)" }}>Calculator Corrupt</h2>
        <p style={{ textAlign: "center", maxWidth: "30rem" }}>
          You can delete the corrupted calculator or export your data for
          troubleshooting.
        </p>
        <button
          className="rectangular-button inverse-color"
          style={{ backgroundSize: "100rem" }}
          type="button"
          onClick={() => removeUnit(calc.id)}
        >
          Delete corrupted calculator
        </button>
      </div>
    );
  }
}

export default AdvancedCalculatorUnit;
